/*
 * Smooth in-page anchor scrolling — global.
 *
 * Game-page CTAs ("Learn More" / "Order Now" in hero-game-page.liquid → button.liquid)
 * are plain <a href> links whose URL is a same-page target:
 *   - "/pages/otherscape#featured-filter"            (native hash → instant jump)
 *   - "…/legend-in-the-mist?scroll=who_you"          (query param → full reload, no scroll)
 * Smooth scrolling was only ever wired to the sticky top nav (.sticky-nav__menu a),
 * never to these buttons. This intercepts same-page anchor/scroll clicks anywhere on
 * the site and glides to the target instead of jumping.
 *
 * Deliberately NOT scrollIntoView({behavior:'smooth'}) — WebKit's native curve is too
 * abrupt on tall pages. The scroll is hand-animated so the feel matches
 * litm-launch-sidebar.liquid, the theme's other bespoke scroller.
 *
 * Safe by construction: it never fires when another handler already claimed the click
 * (respects e.defaultPrevented — so popups via handleButtonClick, the sticky nav, and
 * the launch sidebar all keep their own behaviour), never on new-tab / modified clicks,
 * and never when the target element is missing (lets the browser do its default thing).
 */
(function () {
  'use strict';

  // Feel — mirrors litm-launch-sidebar.liquid.
  var SCROLL_MIN_MS = 600;   // floor, so short hops still read as a glide
  var SCROLL_MAX_MS = 1400;  // ceiling, so a full-page jump never drags
  var SCROLL_MS_PER_PX = 0.5;

  // Fallback top offset (clears the fixed sticky-nav) when the target itself
  // declares no scroll-margin-top. Override globally with
  // `:root { --anchor-scroll-offset: 96px; }` or per-target with `scroll-margin-top`.
  var DEFAULT_OFFSET = 80;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var rafId = null;

  function rootOffset() {
    var raw = getComputedStyle(document.documentElement)
      .getPropertyValue('--anchor-scroll-offset');
    var n = parseFloat(raw);
    return isNaN(n) ? DEFAULT_OFFSET : n;
  }

  // How far the section wants to sit from the top: its own scroll-margin-top if
  // it sets one, otherwise the global fallback that clears the sticky nav.
  function offsetFor(el) {
    var margin = parseFloat(getComputedStyle(el).scrollMarginTop) || 0;
    return margin > 0 ? margin : rootOffset();
  }

  // Document Y where `el`'s top lands just below the offset, clamped to the real
  // scroll range so the last section (which can't reach the top) still settles.
  function scrollTargetFor(el) {
    var y = el.getBoundingClientRect().top + window.pageYOffset - offsetFor(el);
    var max = document.documentElement.scrollHeight - window.innerHeight;
    return Math.max(0, Math.min(y, max));
  }

  function easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  function animateScrollTo(y) {
    if (rafId) cancelAnimationFrame(rafId);

    var start = window.pageYOffset;
    var delta = y - start;
    if (Math.abs(delta) < 1) return;
    if (reduceMotion.matches) { window.scrollTo(0, y); return; }

    var duration = Math.min(
      SCROLL_MAX_MS,
      Math.max(SCROLL_MIN_MS, Math.abs(delta) * SCROLL_MS_PER_PX)
    );

    // Hand control straight back if the user grabs the page mid-flight. Only real
    // input counts — listening for 'scroll' would catch our own scrollTo and abort
    // on the first frame.
    var aborted = false;
    var abort = function () { aborted = true; };
    var events = ['wheel', 'touchstart', 'keydown'];
    events.forEach(function (name) {
      window.addEventListener(name, abort, { passive: true });
    });
    var cleanup = function () {
      rafId = null;
      events.forEach(function (name) {
        window.removeEventListener(name, abort, { passive: true });
      });
    };

    var startTime = null;
    var step = function (now) {
      if (aborted) { cleanup(); return; }
      if (startTime === null) startTime = now;
      var t = Math.min(1, (now - startTime) / duration);
      window.scrollTo(0, start + delta * easeInOutCubic(t));
      if (t < 1) rafId = requestAnimationFrame(step);
      else cleanup();
    };
    rafId = requestAnimationFrame(step);
  }

  function samePath(url) {
    // Compare pathname only; trailing-slash tolerant.
    var a = url.pathname.replace(/\/+$/, '');
    var b = window.location.pathname.replace(/\/+$/, '');
    return url.host === window.location.host && a === b;
  }

  // Returns the id this link targets on the CURRENT page, or null if it isn't a
  // same-page anchor/scroll link we should handle.
  function targetIdFor(anchor) {
    var url;
    try { url = new URL(anchor.href, window.location.href); }
    catch (e) { return null; }
    if (!samePath(url)) return null;

    if (url.hash && url.hash.length > 1) {
      return decodeURIComponent(url.hash.slice(1));
    }
    var scrollId = url.searchParams.get('scroll');
    return scrollId || null;
  }

  document.addEventListener('click', function (e) {
    // Let anything that already handled the click win (popups, sticky nav, sidebar).
    if (e.defaultPrevented) return;
    // Respect new-tab / new-window / download intents.
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    var anchor = e.target.closest && e.target.closest('a[href]');
    if (!anchor) return;
    if (anchor.target && anchor.target !== '_self') return;
    if (anchor.hasAttribute('download')) return;
    if (anchor.closest('[data-no-smooth-scroll]')) return;

    var id = targetIdFor(anchor);
    if (!id) return;

    var target = document.getElementById(id);
    if (!target) return; // unknown target — leave default behaviour alone

    e.preventDefault();
    animateScrollTo(scrollTargetFor(target));
    // Reflect the position in the URL for hash links (keeps back-button/share sane);
    // leave ?scroll= links untouched so we don't rewrite their query.
    if (anchor.hash && anchor.hash.length > 1) {
      try { history.replaceState(null, '', anchor.hash); } catch (e2) {}
    }
  });

  // Landing with ?scroll=<id> in the URL (e.g. a cross-page CTA): glide to it once
  // the page has laid out, then strip the param so a refresh won't re-scroll.
  function handleLoadScroll() {
    var scrollId;
    try {
      scrollId = new URLSearchParams(window.location.search).get('scroll');
    } catch (e) { return; }
    if (!scrollId) return;

    var target = document.getElementById(scrollId);
    if (target) {
      // A tick after layout so image-driven reflow doesn't leave us short.
      requestAnimationFrame(function () {
        animateScrollTo(scrollTargetFor(target));
      });
    }
    try {
      var params = new URLSearchParams(window.location.search);
      params.delete('scroll');
      var qs = params.toString();
      history.replaceState(null, '', window.location.pathname + (qs ? '?' + qs : '') + window.location.hash);
    } catch (e3) {}
  }

  if (document.readyState === 'complete') {
    handleLoadScroll();
  } else {
    window.addEventListener('load', handleLoadScroll);
  }
})();
